import { useEffect, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { Controller, UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import TextInput from "@/components/shared/forms/TextInput";
import DateInput from "@/components/shared/forms/DateInput";
import SearchComboBox, {
  type ComboBoxOption,
} from "@/components/shared/forms/SearchComboBox";
import { type CLEN } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import {
  FaChalkboardTeacher,
  FaTrashAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useCLENFormLogic } from "../../useCLENFormLogic";
import { validateMinDate } from "@/utils/util";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type CLENWizardFormProps = {
  form: UseFormReturn<CLEN, any, CLEN>;
  preschools: PreschoolSummary[];
  classroomOptions?: ComboBoxOption[];
  classroomNumber?: number;
  onRemove?: () => void;
};

export function CLENWizardForm({
  form,
  preschools,
  classroomOptions = [],
  classroomNumber = 1,
  onRemove,
}: CLENWizardFormProps) {
  const coreData = useCoreData();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("CLEN");

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const { watchedFields, validators } = useCLENFormLogic(control);

  const {
    watchLocalClassroomId,
    watchCSPPID,
    watchLocalChildId,
  } = watchedFields;

  const { validateEnrollmentStart, validateEnrollmentEnd } = validators;

  const [collapsed, setCollapsed] = useState(false);

  const hasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (hasErrors) setCollapsed(false);
  }, [hasErrors]);

  const headerStyle = {
    backgroundColor: "#F9FAFB",
    padding: "14px 20px",
    cursor: "pointer",
    userSelect: "none" as const,
  };

  const Wrapper = classroomNumber === 1 ? Container : "div";
  const wrapperProps =
    classroomNumber === 1
      ? {
          className: "ps-3 pe-3 overview-container",
          style: { borderTop: "none", borderRadius: 0 } as React.CSSProperties,
        }
      : {
          className: "ps-3 pe-3",
        };

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="form-card-modern mb-3"
        style={{
          overflow: "hidden",
          padding: 0,
          ...(onRemove
            ? {
                border: "2px dashed #c5ccd4",
                borderRadius: "8px",
              }
            : {}),
          ...(classroomOptions.length > 0 && !collapsed
            ? { paddingBottom: 180 }
            : {}),
        }}
      >
        <div
          className="section-header d-flex justify-content-between align-items-center"
          style={{
            ...headerStyle,
            width: "100%",
            ...(collapsed ? { marginBottom: 0 } : {}),
          }}
        >
          <button
            type="button"
            className="d-flex align-items-center gap-2 flex-grow-1"
            style={{
              border: "none",
              background: "none",
              font: "inherit",
              textAlign: "left" as const,
              padding: 0,
            }}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            <FaChalkboardTeacher className="section-icon" />
            <h3 className="section-title mb-0">
              {classroomNumber === 1
                ? "Classroom Enrollment"
                : `Classroom Enrollment ${classroomNumber}`}
            </h3>
          </button>
          <div className="d-flex align-items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  color: "#dc3545",
                  border: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
                onClick={onRemove}
              >
                <FaTrashAlt style={{ fontSize: "12px" }} />
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              style={{ border: "none", background: "transparent", padding: 0 }}
              onClick={() => setCollapsed((prev) => !prev)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expand section" : "Collapse section"}
            >
              {collapsed ? (
                <FaChevronDown style={{ fontSize: "12px", color: "#6c757d" }} />
              ) : (
                <FaChevronUp style={{ fontSize: "12px", color: "#6c757d" }} />
              )}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div style={{ padding: "20px" }}>
            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="ProgramYear"
                  labelText="Program Year"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={control}
                      name={name}
                      rules={{ required: "Program Year is required" }}
                      options={coreData.programYearList}
                    />
                  )}
                </FormFieldRow>
              </div>
              <div className="col-md-4">
                <FormFieldRow
                  name="PreschoolCode"
                  labelText="Preschool"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={control}
                      name={name}
                      rules={{ required: "Preschool is required" }}
                      options={preschools.map((x) => ({
                        Text: x.PreschoolName,
                        Value: x.PreschoolCDSCode,
                      }))}
                    />
                  )}
                </FormFieldRow>
              </div>
              <div className="col-md-4">
                <FormFieldRow
                  name="CSPPID"
                  labelText="CSPPID"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      readOnly
                      maxLength={50}
                      value={watchCSPPID}
                      {...register(name)}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="LocalChildId"
                  labelText="Local Child ID (CICN)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      readOnly
                      maxLength={50}
                      value={watchLocalChildId}
                      {...register(name, {
                        required: "Local Child ID (CICN) is required",
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="LocalClassroomId"
                  labelText="Classroom ID"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) =>
                    classroomOptions.length > 0 ? (
                      <Controller
                        name="LocalClassroomId"
                        control={control}
                        rules={{ required: "Classroom ID is required" }}
                        render={({ field }) => (
                          <SearchComboBox
                            id={id}
                            options={classroomOptions}
                            value={field.value}
                            placeholder="Search by classroom name or ID"
                            noMatchMessage="No matching classrooms found"
                            hasError={!!errors.LocalClassroomId}
                            isRequired
                            ariaDescribedBy={`${id}-description`}
                            onChange={(value) => {
                              setValue("LocalClassroomId", value, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextInput
                        id={id}
                        disabled={false}
                        maxLength={50}
                        value={watchLocalClassroomId}
                        {...register(name, {
                          required: "Classroom ID is required",
                        })}
                      />
                    )
                  }
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="ClassroomEnrollmentStart"
                  labelText="Classroom Enrollment Start Date"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  tooltip={tooltipMap?.get("ClassroomStart")}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <DateInput
                      id={id}
                      disabled={false}
                      max="9999-12-31"
                      {...register(name, {
                        required: "Classroom Enrollment Start Date is required",
                        validate: (value) => {
                          const minCheck = validateMinDate(value);
                          if (minCheck !== true) return minCheck;
                          return validateEnrollmentStart(value);
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="ClassroomEnrollmentEnd"
                  labelText="Classroom Enrollment End Date"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  tooltip={tooltipMap?.get("ClassroomEnd")}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <DateInput
                      id={id}
                      disabled={false}
                      max="9999-12-31"
                      {...register(name, {
                        validate: (value) => {
                          if (!value) return true;
                          const minCheck = validateMinDate(value);
                          if (minCheck !== true) return minCheck;
                          return validateEnrollmentEnd(value);
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <span
              id="spnAddOrEditClassroomErrorOverall"
              className="row ps-3 mt-3 pe-3 float-end"
              style={{ color: "#CC0000" }}
            />
          </div>
        )}
      </div>
    </Wrapper>
  );
}
